import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import { mapLanguageLocale } from '@apps/main/modules/localization/localeUtils';
import type { MarkerData } from '@apps/main/modules/state/mapState';
import {
  MapStateKey,
  RecipeStateKey,
  TripStateKey,
} from '@apps/main/modules/state/state';
import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import { useItineraryStore } from '@apps/main/modules/store/itineraryStore';
import {
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { MapComponent } from '@apps/main/new-desktop/modules/maps';

import { injectStrict } from '@libs/utils';
import type { Map, MapMovedEvent, Marker } from '@libs/maps';
import { MapControl } from '@libs/maps';

import type { HighlightedHotel, HotelMapCardData } from '../../models';
import { useHotelSelectionStore } from '../hotelSelection/hotelSelectionStore';
import { sortDestinationProducts } from '../hotelSelection/hotelSelectionStoreLogic';

import {
  getClusterHotelMarkerFormatter,
  getClusterOptions,
  getHighlightedHotel,
  getHotelMapCardData,
  getHotelSelectionMarkers,
  getMarkerFormatters,
} from './hotelSelectionMapStoreLogic';

type MapProperties = InstanceType<typeof MapComponent>['$props'];

export const useHotelSelectionMapStore = defineStore(
  'HotelSelectionMapStore',
  () => {
    const mapState = injectStrict(MapStateKey);
    const tripState = injectStrict(TripStateKey);
    const recipeState = injectStrict(RecipeStateKey);
    const hotelSelectionStore = useHotelSelectionStore();
    const editingIngredientStore = useEditingIngredientStore();
    const itineraryStore = useItineraryStore();
    const statsigEventsStore = useStatsigEventsStore();
    const routerStore = useRouterStore();

    // State
    const isMapCreated = ref<boolean>(false);
    const isFirstMapRendered = ref<boolean>(true);
    const highlightedHotel = ref<HighlightedHotel | undefined>(undefined);

    // Actions
    function setDynamicMapIntegration(integration: Map): void {
      isFirstMapRendered.value = false;
      isMapCreated.value = true;
      mapState.setMapIntegration(integration);
    }

    function mapViewChanged(event: MapMovedEvent): void {
      mapState.setMapMovedAndCreateNewPolygon(event);
    }

    function markerClicked(marker: Marker): void {
      statsigEventsStore.logHotelSelectionMapMarkerClicked();
      const data = marker.data as MarkerData;
      const ingredientIndex = recipeState.getIngredientIndex(data.ingredientId);
      highlightedHotel.value = {
        ingredientId: data.ingredientId,
        ingredientIndex,
        hotelCode: data.hotelCode,
        showHotelMapCard: true,
      };
    }

    function hotelCardClicked(): void {
      if (!highlightedHotel.value || !hotelMapCardData.value) {
        throw new Error('Not possible!');
      }

      const hotelId = highlightedHotel.value;
      const index = recipeState.getIngredientIndex(hotelId.ingredientId);
      routerStore.navigateToHotelDetailsInHotelSelection(
        index,
        hotelId.hotelCode
      );
    }

    function hideHotelCard(): void {
      highlightedHotel.value = undefined;
    }

    function toggleSearchByMovingMap(): void {
      mapState.searchByMapMovement.value = !mapState.searchByMapMovement.value;
    }

    function hightlightOnMap(hotelCode: string | undefined): void {
      const ingredientId =
        editingIngredientStore.editingStayIngredient?.ingredientId;

      if (ingredientId) {
        const ingredientIndex = recipeState.getIngredientIndex(ingredientId);
        highlightedHotel.value = getHighlightedHotel(
          hotelCode,
          ingredientId,
          ingredientIndex
        );
      }
    }

    function destroyMap(): void {
      mapState.mapShouldCenterOnMarkers.value = true;
    }

    // Getters
    const mapProperties = computed<MapProperties>(() => ({
      markers: getHotelSelectionMarkers(
        mapState.markers.value,
        highlightedHotel.value,
        topAvailableProductCodes.value
      ),
      markerFormatters: getMarkerFormatters(),
      clusterMarkerFormatters: getClusterHotelMarkerFormatter(),
      clusterOptions: getClusterOptions(),
      route: mapState.mapRoute.value,
      showAllMarkers: mapState.mapShouldCenterOnMarkers.value,
      startRectangle: mapState.DEFAULT_RECTANGLE,
      gestureHandling: 'greedy',
      locale: mapLanguageLocale,
      mapControls: [MapControl.zoomControl, MapControl.mapTypeControl],
    }));

    const topAvailableProductCodes = computed<string[]>(() => {
      const TOP_NUMBER_HOTELS = 10;
      let selectedHotelCode: string | undefined;

      if (editingIngredientStore.editingStayIngredient?.ingredientId) {
        const hotelFromItinerary = itineraryStore.getHotelIngredientResponse(
          editingIngredientStore.editingStayIngredient.ingredientId
        );

        if (
          hotelFromItinerary &&
          hotelFromItinerary.suggested &&
          hotelFromItinerary.suggested.code
        ) {
          selectedHotelCode = hotelFromItinerary.suggested.code;
        }
      }

      const availableProducts = sortDestinationProducts(
        tripState.destinationProducts.value,
        hotelSelectionStore.listSuggestionType,
        selectedHotelCode
      );

      return availableProducts
        .map((product) => product.hotelCode)
        .slice(0, TOP_NUMBER_HOTELS + (selectedHotelCode ? 1 : 0));
    });

    const isSettingMap = computed<boolean>(
      () =>
        !tripState.isLocationManuallyChanged.value &&
        !mapState.startingLocation.value
    );

    const searchByMovingMap = computed<boolean>(
      () => mapState.searchByMapMovement.value
    );

    const hotelMapCardData = computed<HotelMapCardData | undefined>(() =>
      getHotelMapCardData(
        highlightedHotel.value,
        tripState.getStaticInfo,
        getImageUrl
      )
    );

    return {
      // State
      isFirstMapRendered,
      isMapCreated,
      highlightedHotel,

      // Actions
      mapViewChanged,
      markerClicked,
      setDynamicMapIntegration,
      toggleSearchByMovingMap,
      hotelCardClicked,
      hideHotelCard,
      hightlightOnMap,
      destroyMap,

      // Getters
      mapProperties,
      isSettingMap,
      searchByMovingMap,
      hotelMapCardData,
    };
  }
);
